import { refinementKeyToAlgoliaNumericAttribute } from '../algolia/refinement-key-to-algolia-numeric-attribute';
import { DEFAULT_TYPESENSE_SEARCH_CONFIG } from './collection-schemas';
import { buildTypesenseSortBy, parseSortFromAlgoliaIndex } from './algolia-index-to-collection';
import { buildTypesenseGeoFilterFromAlgoliaInsideBoundingBox } from './build-typesense-geo-filter';
import { isSrpNonFilterQueryParam } from '../search/srp-non-filter-query-params';

export interface AlgoliaLikeSearchParams {
  query?: string;
  page?: number;
  hitsPerPage?: number;
  facets?: string[];
  facetFilters?: (string | string[])[];
  numericFilters?: string[];
  filters?: string;
  /** Algolia map geo search: [[south, west, north, east]] */
  insideBoundingBox?: number[][];
}

export interface TypesenseSearchParams {
  q: string;
  query_by: string;
  include_fields: string;
  per_page: number;
  page: number;
  filter_by?: string;
  sort_by?: string;
  facet_by?: string;
  max_facet_values?: number;
  drop_tokens_threshold: number;
  typo_tokens_threshold: number;
  min_len_1typo: number;
  min_len_2typo: number;
}

const NUMERIC_RULE: Record<string, 'gte' | 'lte'> = {
  salaryRangeMin: 'gte',
  vacationWeeks: 'gte',
  companyHeadcountRangeMax: 'lte',
  lastSeenMin: 'gte',
};

function parseFacetFilterEntry(entry: string): { attribute: string; value: string; exclude: boolean } | null {
  const excludeMatch = entry.match(/^NOT\s+([^:]+):(.+)$/);
  if (excludeMatch) {
    return { attribute: excludeMatch[1], value: excludeMatch[2], exclude: true };
  }
  const includeMatch = entry.match(/^([^:]+):(.+)$/);
  if (includeMatch) {
    return { attribute: includeMatch[1], value: includeMatch[2], exclude: false };
  }
  return null;
}

function escapeFilterValue(value: string): string {
  return `\`${value.replace(/`/g, '\\`')}\``;
}

/** Typesense bool facets use `field:=true` without backticks (Algolia sends `field:true`). */
const TYPESENSE_BOOL_FACET_ATTRIBUTES = new Set([
  'isFinland',
  'isTyomarkkinatori',
  'isActive',
  'isOpenApplication',
  'isSummerJob',
  'isDriversLicenseRequired',
  'salaryIsEstimate',
]);

function buildBoolFacetFilterClause(
  attribute: string,
  value: string,
  exclude: boolean,
): string | null {
  if (value !== 'true' && value !== 'false') {
    return null;
  }
  const field = typesenseFilterAttribute(attribute);
  if (exclude) {
    return `${field}:!=${value}`;
  }
  return `${field}:=${value}`;
}

/** Algolia uses objectID; Typesense job collections use id. */
function typesenseFilterAttribute(attribute: string): string {
  return attribute === 'objectID' ? 'id' : attribute;
}

/**
 * Remote-hub pseudo-locations map to the remoteness facet. The indexable remote hub
 * SRPs (/en/jobs/location/Remote_-_Finland etc.) must serve the full remote inventory
 * (~3k live listings), not only the jobs whose listed location is literally
 * "Remote - Finland" (~30). Each pseudo-location value only exists in its own locale's
 * collection, so the mapped remoteness value is locale-correct by construction.
 * "Remote - Europe" hubs keep literal location filtering — their intent is
 * region-scoped, not remoteness-scoped.
 */
const REMOTE_FINLAND_LOCATION_TO_REMOTENESS: Record<string, string> = {
  'Remote - Finland': 'Remote',
  'Etätyö - Suomi': 'Etätyö',
  'Distans - Finland': 'Distans',
};

/** Include-clause for a facet atom, applying the remote-hub location→remoteness mapping. */
function buildIncludeFacetClause(attribute: string, value: string): string {
  const remoteness = attribute === 'location' ? REMOTE_FINLAND_LOCATION_TO_REMOTENESS[value] : undefined;
  if (remoteness) {
    return `remoteness:=${escapeFilterValue(remoteness)}`;
  }
  return `${typesenseFilterAttribute(attribute)}:=${escapeFilterValue(value)}`;
}

function buildFacetGroupClauses(facetFilters: (string | string[])[]): string[] {
  const groups: string[] = [];

  for (const entry of facetFilters) {
    if (Array.isArray(entry)) {
      const orClauses = entry
        .map(parseFacetFilterEntry)
        .filter(
          (p): p is NonNullable<typeof p> =>
            p !== null && !p.exclude && !isSrpNonFilterQueryParam(p.attribute),
        )
        .map((p) => buildIncludeFacetClause(p.attribute, p.value));
      if (orClauses.length === 1) {
        groups.push(orClauses[0]);
      } else if (orClauses.length > 1) {
        groups.push(`(${orClauses.join(' || ')})`);
      }
      continue;
    }

    const parsed = parseFacetFilterEntry(entry);
    if (!parsed || isSrpNonFilterQueryParam(parsed.attribute)) continue;

    const field = typesenseFilterAttribute(parsed.attribute);
    if (parsed.exclude) {
      const boolClause = TYPESENSE_BOOL_FACET_ATTRIBUTES.has(parsed.attribute)
        ? buildBoolFacetFilterClause(parsed.attribute, parsed.value, true)
        : null;
      groups.push(boolClause ?? `${field}:!=${escapeFilterValue(parsed.value)}`);
    } else {
      const boolClause = TYPESENSE_BOOL_FACET_ATTRIBUTES.has(parsed.attribute)
        ? buildBoolFacetFilterClause(parsed.attribute, parsed.value, false)
        : null;
      if (
        !parsed.exclude &&
        parsed.attribute === 'isFinland' &&
        parsed.value === 'true'
      ) {
        // Työmarkkinatori jobs are always Finland; include when isFinland was missing on older index rows.
        groups.push('(isFinland:=true || isTyomarkkinatori:=true)');
      } else {
        groups.push(boolClause ?? buildIncludeFacetClause(parsed.attribute, parsed.value));
      }
    }
  }

  return groups;
}

/** Split on a separator only at the top level (outside parentheses). */
function splitTopLevel(input: string, separator: ' AND ' | ' OR '): string[] {
  const parts: string[] = [];
  let depth = 0;
  let current = '';
  let i = 0;
  while (i < input.length) {
    const ch = input[i];
    if (ch === '(') depth += 1;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    if (depth === 0 && input.startsWith(separator, i)) {
      parts.push(current);
      current = '';
      i += separator.length;
      continue;
    }
    current += ch;
    i += 1;
  }
  parts.push(current);
  return parts.map((p) => p.trim()).filter(Boolean);
}

/** Strip one layer of parentheses only when they wrap the entire expression. */
function unwrapOuterParens(input: string): string {
  let s = input.trim();
  while (s.startsWith('(') && s.endsWith(')')) {
    let depth = 0;
    let wrapsWhole = true;
    for (let i = 0; i < s.length; i += 1) {
      if (s[i] === '(') depth += 1;
      else if (s[i] === ')') depth -= 1;
      if (depth === 0 && i < s.length - 1) {
        wrapsWhole = false;
        break;
      }
    }
    if (!wrapsWhole) break;
    s = s.slice(1, -1).trim();
  }
  return s;
}

function stripQuotes(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"')) {
    return trimmed.slice(1, -1).replace(/\\"/g, '"').replace(/\\\\/g, '\\');
  }
  return trimmed;
}

/** Translate a single Algolia filter atom (e.g. `NOT objectID:x`, `location:"Y"`, `salaryRangeMin>=10`). */
function buildClauseFromFilterAtom(atom: string): string | null {
  const trimmed = atom.trim().replace(/^\((.*)\)$/s, '$1').trim();
  if (!trimmed) return null;

  const numeric = trimmed.match(/^([A-Za-z0-9_]+)\s*(>=|<=|>|<|=)\s*(-?\d+(?:\.\d+)?)$/);
  if (numeric) {
    const op = numeric[2] === '=' ? ':=' : `:${numeric[2]}`;
    return `${typesenseFilterAttribute(numeric[1])}${op}${numeric[3]}`;
  }

  const facet = trimmed.match(/^(NOT\s+)?([A-Za-z0-9_]+):(.+)$/s);
  if (!facet) return null;
  const exclude = Boolean(facet[1]);
  const attribute = facet[2];
  if (isSrpNonFilterQueryParam(attribute)) return null;
  const value = stripQuotes(facet[3]);
  const field = typesenseFilterAttribute(attribute);

  if (TYPESENSE_BOOL_FACET_ATTRIBUTES.has(attribute)) {
    const boolClause = buildBoolFacetFilterClause(attribute, value, exclude);
    if (boolClause) return boolClause;
  }
  return exclude
    ? `${field}:!=${escapeFilterValue(value)}`
    : buildIncludeFacetClause(attribute, value);
}

/**
 * Translate an Algolia `filters` string into Typesense `filter_by` clauses.
 * Supports top-level ` AND `, parenthesised/inline ` OR ` groups, `NOT`, quoted values,
 * and numeric comparisons. Consumers that pass a raw `filters` string (similar jobs,
 * locations hub, objectID lookups) previously had it silently dropped under Typesense.
 */
function buildFilterByFromFiltersString(filters: string): string[] {
  const conjuncts = splitTopLevel(filters.trim(), ' AND ');
  const clauses: string[] = [];
  for (const conjunct of conjuncts) {
    const orParts = splitTopLevel(unwrapOuterParens(conjunct), ' OR ');
    if (orParts.length > 1) {
      const orClauses = orParts
        .map(buildClauseFromFilterAtom)
        .filter((c): c is string => c !== null);
      if (orClauses.length === 1) clauses.push(orClauses[0]);
      else if (orClauses.length > 1) clauses.push(`(${orClauses.join(' || ')})`);
      continue;
    }
    const clause = buildClauseFromFilterAtom(conjunct);
    if (clause) clauses.push(clause);
  }
  return clauses;
}

function buildNumericClauses(numericFilters: string[]): string[] {
  const clauses: string[] = [];
  for (const filter of numericFilters) {
    const gteMatch = filter.match(/^([^><=]+)>=(.+)$/);
    const lteMatch = filter.match(/^([^><=]+)<=(.+)$/);
    if (gteMatch) {
      clauses.push(`${gteMatch[1]}:>=${gteMatch[2]}`);
    } else if (lteMatch) {
      clauses.push(`${lteMatch[1]}:<=${lteMatch[2]}`);
    }
  }
  return clauses;
}

function buildFilterByFromObjectFilters(filters: Record<string, unknown>): string[] {
  const clauses: string[] = [];

  for (const [attribute, raw] of Object.entries(filters)) {
    const isExclude = attribute.startsWith('exclude_');
    const actualAttribute = isExclude ? attribute.replace('exclude_', '') : attribute;
    const values = Array.isArray(raw) ? raw : [raw];

    if (NUMERIC_RULE[actualAttribute] && !isExclude) {
      for (const v of values) {
        const num = typeof v === 'number' ? v : Number(String(v).trim());
        if (!Number.isFinite(num)) continue;
        const tsAttribute = refinementKeyToAlgoliaNumericAttribute(actualAttribute);
        if (NUMERIC_RULE[actualAttribute] === 'gte') {
          clauses.push(`${tsAttribute}:>=${num}`);
        } else {
          clauses.push(`${tsAttribute}:<=${num}`);
        }
      }
    } else if (!NUMERIC_RULE[actualAttribute]) {
      const stringValues = values.map((v) => String(v));
      if (isExclude) {
        for (const v of stringValues) {
          clauses.push(`${actualAttribute}:!=${escapeFilterValue(v)}`);
        }
      } else if (stringValues.length === 1) {
        clauses.push(`${actualAttribute}:=${escapeFilterValue(stringValues[0])}`);
      } else if (stringValues.length > 1) {
        clauses.push(
          `(${stringValues.map((v) => `${actualAttribute}:=${escapeFilterValue(v)}`).join(' || ')})`,
        );
      }
    }
  }

  return clauses;
}

export function buildTypesenseSearchParams(
  algoliaParams: AlgoliaLikeSearchParams,
  options?: { indexName?: string; sort?: 'relevance' | 'date' | 'salary' },
): TypesenseSearchParams {
  const sort =
    options?.sort ??
    (options?.indexName ? parseSortFromAlgoliaIndex(options.indexName) : 'relevance');

  const filterClauses: string[] = [];

  if (algoliaParams.facetFilters?.length) {
    filterClauses.push(...buildFacetGroupClauses(algoliaParams.facetFilters));
  }
  if (algoliaParams.numericFilters?.length) {
    filterClauses.push(...buildNumericClauses(algoliaParams.numericFilters));
  }
  if (algoliaParams.insideBoundingBox?.length) {
    const geoFilter = buildTypesenseGeoFilterFromAlgoliaInsideBoundingBox(
      algoliaParams.insideBoundingBox,
    );
    if (geoFilter) {
      filterClauses.push(geoFilter);
    }
  }
  if (algoliaParams.filters?.trim()) {
    filterClauses.push(...buildFilterByFromFiltersString(algoliaParams.filters));
  }

  const queryText = algoliaParams.query?.trim() ?? '';
  const isGeoSearch = Boolean(algoliaParams.insideBoundingBox?.length);
  const perPage = algoliaParams.hitsPerPage ?? DEFAULT_TYPESENSE_SEARCH_CONFIG.per_page;
  const page = Math.max(1, (algoliaParams.page ?? 0) + 1);

  const params: TypesenseSearchParams = {
    q: queryText || (isGeoSearch ? '*' : ''),
    query_by: DEFAULT_TYPESENSE_SEARCH_CONFIG.query_by,
    include_fields: DEFAULT_TYPESENSE_SEARCH_CONFIG.include_fields,
    per_page: perPage,
    page,
    drop_tokens_threshold: DEFAULT_TYPESENSE_SEARCH_CONFIG.drop_tokens_threshold,
    typo_tokens_threshold: DEFAULT_TYPESENSE_SEARCH_CONFIG.typo_tokens_threshold,
    min_len_1typo: DEFAULT_TYPESENSE_SEARCH_CONFIG.min_len_1typo,
    min_len_2typo: DEFAULT_TYPESENSE_SEARCH_CONFIG.min_len_2typo,
  };

  if (algoliaParams.facets?.length) {
    params.facet_by = algoliaParams.facets.join(',');
    params.max_facet_values = 100;
  }

  const sortBy = buildTypesenseSortBy(sort);
  if (sortBy && !isGeoSearch) {
    params.sort_by = sortBy;
  }

  if (filterClauses.length > 0) {
    params.filter_by = filterClauses.join(' && ');
  }

  return params;
}

export function buildTypesenseFilterByFromObject(
  filters: Record<string, unknown>,
  options?: { isFinlandEnabled?: boolean },
): string | undefined {
  const clauses = buildFilterByFromObjectFilters(filters);
  if (options?.isFinlandEnabled) {
    clauses.push('(isFinland:=true || isTyomarkkinatori:=true)');
  }
  return clauses.length > 0 ? clauses.join(' && ') : undefined;
}
